import React, { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { iconForTopic, TOPICS } from "../../utils/materialTaxonomy";

// Kept here rather than in a stylesheet: the pills are one continuous row
// that loops, which needs a keyframe the utility classes can't express, and
// this component is the only thing that ever uses it.
const MARQUEE_CSS = `
@keyframes subject-marquee-scroll {
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
}
.subject-marquee-viewport {
  overflow: hidden;
  -webkit-mask-image: linear-gradient(to right, transparent, black 24px, black calc(100% - 24px), transparent);
  mask-image: linear-gradient(to right, transparent, black 24px, black calc(100% - 24px), transparent);
}
.subject-marquee-track {
  display: flex;
  flex-wrap: nowrap;
  width: max-content;
  animation: subject-marquee-scroll 45s linear infinite;
}
.subject-marquee-track:hover {
  animation-play-state: paused;
}
@media (prefers-reduced-motion: reduce) {
  .subject-marquee-track { animation: none; }
}
`;

const SubjectBrowser = ({ token, selectedTopic, onSelectSubject }) => {
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/materials/topics', { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => (res.ok ? res.json() : { topics: [] }))
      .then((data) => {
        if (cancelled) return;
        const map = {};
        (data.topics || []).forEach(({ topic, count }) => { map[topic] = count; });
        setCounts(map);
      })
      .catch(() => { if (!cancelled) setCounts({}); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  // Always show every known subject — including ones with zero materials —
  // so the strip reads as a complete map of the library, not just whatever
  // happens to have content today. Populated subjects float to the front.
  const allSubjects = useMemo(() => {
    const known = new Set(TOPICS);
    const extra = Object.keys(counts).filter((t) => !known.has(t));
    return [...TOPICS, ...extra]
      .map((topic) => ({ topic, count: counts[topic] || 0 }))
      .sort((a, b) => b.count - a.count);
  }, [counts]);

  const renderPill = (topic, count, keyPrefix) => {
    const Icon = iconForTopic(topic);
    const isActive = selectedTopic === topic;
    const isEmpty = count === 0;
    return (
      <motion.button
        key={`${keyPrefix}-${topic}`}
        onClick={() => !isEmpty && onSelectSubject?.(isActive ? null : topic)}
        whileHover={!isEmpty ? { y: -2 } : undefined}
        whileTap={!isEmpty ? { scale: 0.97 } : undefined}
        className={`flex-shrink-0 px-5 py-3 mr-2.5 rounded-2xl border transition-colors duration-300 flex items-center gap-3 group ${
          isEmpty ? "opacity-40 cursor-default border-border bg-card/30 text-muted-foreground" :
          isActive
            ? "bg-primary border-primary text-primary-foreground"
            : "border-border bg-card/50 hover:bg-primary/5 hover:border-primary/30 text-foreground"
        }`}
      >
        <Icon className={`w-4 h-4 ${isActive && !isEmpty ? "" : "text-primary"}`} />
        <span className="text-xs font-bold whitespace-nowrap tracking-tight">
          {topic}
        </span>
        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${isActive && !isEmpty ? "bg-primary-foreground/20" : "bg-muted text-muted-foreground"}`}>
          {count}
        </span>
      </motion.button>
    );
  };

  return (
    <div className="mb-6 mt-12">
      <h3 className="section-title text-base mt-0 mb-3">Browse by Subject</h3>

      {loading ? (
        <div className="flex gap-2.5 overflow-x-auto scrollbar-hide pb-1">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex-shrink-0 w-32 h-[52px] rounded-2xl bg-muted/40 animate-pulse" />
          ))}
        </div>
      ) : (
        <>
          <style>{MARQUEE_CSS}</style>
          <div className="subject-marquee-viewport pb-1">
            <div className="subject-marquee-track">
              {allSubjects.map(({ topic, count }) => renderPill(topic, count, "a"))}
              {allSubjects.map(({ topic, count }) => renderPill(topic, count, "b"))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SubjectBrowser;
